"use client"

import clsx from "clsx/lite"
import RightArrow from "@icons/RightArrow.svg?react"
import { twMerge } from "tailwind-merge"
import { useEffect, useState, useRef } from "react"
import { AnimatePresence, motion } from "motion/react"

export default ({
    page = 0,
    total,
    arrows = true,
    callback = () => {},
    activeColor = "garden-grove",
    inactiveColor = "twilight-gray",
    className,
}) => {
    const [current, setCurrent] = useState(page)
    const isFirstRender = useRef(true)

    const previous = () => {
        setCurrent((prev) => {
            if (prev > 0) return prev - 1
            return prev
        })
    }

    const next = () => {
        setCurrent((prev) => {
            if (prev < total - 1) return prev + 1
            return prev
        })
    }

    // Determine which page indexes to show
    const getVisiblePages = () => {
        if (total <= 3) {
            // If total pages are 3 or less, just show all
            return Array.from({ length: total }, (_, i) => i)
        }

        if (current === 0) {
            return [0, 1, 2]
        } else if (current >= total - 2) {
            return [total - 3, total - 2, total - 1]
        } else {
            return [current - 1, current, current + 1]
        }
    }

    const handlePageClick = (page) => {
        setCurrent(page)
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        callback(current)
    }, [current])

    useEffect(() => {
        setCurrent(page)
    }, [page])

    const visiblePages = getVisiblePages()

    return (
        <>
            {total > 1 && (
                <div
                    className={twMerge(
                        "flex mx-auto items-center group mb-8",
                        className,
                    )}
                >
                    {arrows && (
                        <RightArrow
                            onClick={() => previous()}
                            className={clsx(
                                "md:opacity-0 transition-opacity size-5 rotate-180",
                                `fill-${inactiveColor} hover:fill-${activeColor}`,
                                current !== 0 ?
                                    "group-hover:opacity-100 cursor-pointer"
                                :   "",
                            )}
                        />
                    )}

                    <div className={clsx("flex", arrows ? "mx-4" : "mx-0")}>
                        <AnimatePresence initial={false} mode="popLayout">
                            {visiblePages.map((page) => (
                                <motion.div
                                    layout
                                    key={page}
                                    onClick={() => handlePageClick(page)}
                                    tabIndex={page}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.2 }}
                                    className={clsx(
                                        "mx-1.5 first:ml-0 last:mr-0 border-4 w-8 h-4 cursor-pointer",
                                        page === current ?
                                            `border-${activeColor} bg-${activeColor} z-20`
                                        :   `border-${inactiveColor} hover:border-${activeColor}`,
                                    )}
                                />
                            ))}
                        </AnimatePresence>
                    </div>

                    {arrows && (
                        <RightArrow
                            onClick={() => next()}
                            className={clsx(
                                "md:opacity-0 transition-opacity size-5",
                                `fill-${inactiveColor} hover:fill-${activeColor}`,
                                current !== total - 1 ?
                                    "group-hover:opacity-100 cursor-pointer"
                                :   "",
                            )}
                        />
                    )}
                </div>
            )}
        </>
    )
}
